import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsHeader } from './shared/SettingsHeader';
import { SettingsInput } from './shared/SettingsInput';
import { SettingsRow } from './shared/SettingsRow';
import emailIcon from '@/assets/images/svg/email_icon.svg';

const emailSchema = z.object({
  newEmail: z.string().email('Invalid email address'),
  currentPassword: z.string().min(1, 'Password is required to change email'),
});

type EmailFormValues = z.infer<typeof emailSchema>;

export const EmailSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
  });

  const onSubmit = async (data: EmailFormValues) => {
    try {
      const updatedUser = await api.users.updateEmail(data.newEmail, data.currentPassword);
      updateUser(updatedUser);

      addToast({
        title: 'Email Updated',
        subtitle: `Your email has been changed to ${data.newEmail}`,
        type: 'success',
      });
      setIsEditing(false);
      reset();
    } catch (error) {
      addToast({
        title: 'Update Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  if (!isEditing) {
    return (
      <SettingsRow
        icon={emailIcon}
        title='Email address'
        description={
          <span>
            The current email address of your account is{' '}
            <span className='font-bold'>{user?.email || '...'}</span>
          </span>
        }
      />
    );
  }

  return (
    <SettingsCard className='bg-gray-75'>
      <SettingsHeader
        icon={emailIcon}
        title='Email address'
        description={
          <span>
            The current email address of your account is{' '}
            <span className='font-bold'>{user?.email}</span>
          </span>
        }
        isEditing
      />

      <form onSubmit={handleSubmit(onSubmit)} className='mt-6 space-y-6 pl-0 md:pl-10'>
        <SettingsInput label='Current Email' defaultValue={user?.email} disabled />

        <div>
          <SettingsInput
            label='New Email Address'
            placeholder='e.g. new.email@example.com'
            autoComplete='email'
            {...register('newEmail')}
          />
          {errors.newEmail && (
            <p className='mt-1 text-xs text-red-500'>{errors.newEmail.message}</p>
          )}
          <p className='mt-2 font-sans text-12 text-gray-500'>
            You&apos;ll need to verify your new email address
          </p>
        </div>

        <div>
          <SettingsInput
            label='Current Password'
            type='password'
            placeholder='Required to confirm change'
            autoComplete='current-password'
            {...register('currentPassword')}
          />
          {errors.currentPassword && (
            <p className='mt-1 text-xs text-red-500'>{errors.currentPassword.message}</p>
          )}
        </div>

        <div className='flex items-center justify-between pt-2'>
          <Button
            type='submit'
            variant='settings-modify'
            className='h-[36px] w-[154px]'
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Email'}
          </Button>

          <button
            type='button'
            onClick={handleCancel}
            className='font-sans text-16 font-semibold text-black transition-opacity hover:opacity-70'
          >
            Cancel
          </button>
        </div>
      </form>
    </SettingsCard>
  );
};
